:root {
  color-scheme: light;

  --font-sans: "Manrope Variable", "Manrope", "Segoe UI", system-ui, sans-serif;
  --font-mono: "IBM Plex Mono", "Consolas", ui-monospace, monospace;

  --color-navy: #1a365d;
  --color-green: #22543d;
  --color-gold: #c8aa64;
  --color-brass: #755a1a;
  --color-brass-ui: #8c6d23;
  --color-gray: #4a5568;
  --color-danger: #9b2c2c;
  --color-paper: #f7f9fa;
  --color-cloud: #edf2f5;
  --color-white: #ffffff;

  --color-void: #0b0f19;
  --color-dark-card: #101626;
  --color-dark-raised: #161e33;
  --color-dark-hover: #1e2842;
  --color-dark-line: #232e4c;
  --color-dark-ink: #e9ecf3;
  --color-dark-secondary: #b6c0d4;
  --color-dark-muted: #8291ad;

  --color-success-dark: #34d399;
  --color-warning-dark: #fbbf24;
  --color-error-dark: #f87171;
  --color-info-dark: #7db2ff;

  --surface-canvas: var(--color-cloud);
  --surface-panel: var(--color-white);
  --surface-soft: var(--color-paper);
  --surface-raised: var(--color-white);
  --text-primary: var(--color-navy);
  --text-secondary: var(--color-gray);
  --text-muted: #657383;
  --border-subtle: #d8e1e6;
  --border-strong: #b8c7d0;
  --semantic-success: var(--color-green);
  --semantic-warning: var(--color-brass);
  --semantic-error: var(--color-danger);
  --semantic-info: var(--color-navy);
  --focus-color: var(--color-brass);

  --space-1: 0.25rem;
  --space-2: 0.5rem;
  --space-3: 0.75rem;
  --space-4: 1rem;
  --space-6: 1.5rem;
  --space-8: 2rem;
  --space-12: 3rem;
  --space-16: 4rem;

  --radius-control: 0.625rem;
  --radius-card: 0.875rem;
  --radius-scene: 1.25rem;
  --radius-pill: 999px;

  --shadow-1: 0 0.5rem 1.5rem rgb(16 41 66 / 8%);
  --shadow-2: 0 1rem 2.8125rem rgb(16 41 66 / 12%);
  --shadow-workbench: 0 1.5rem 4rem rgb(5 10 22 / 24%);

  --z-base: 0;
  --z-raised: 10;
  --z-sticky: 20;
  --z-dropdown: 40;
  --z-overlay: 60;
  --z-modal: 80;
  --z-toast: 100;

  --motion-ease: cubic-bezier(0.22, 0.9, 0.35, 1);
  --motion-response: 160ms;
  --motion-entry: 320ms;
  --motion-confirm: 400ms;
  --motion-cinematic: 800ms;

  font-family: var(--font-sans);
  font-synthesis: none;
  text-rendering: optimizeLegibility;
}

[data-motion-resolved="reduced"] {
  --motion-response: 1ms;
  --motion-entry: 1ms;
  --motion-confirm: 1ms;
  --motion-cinematic: 1ms;
}

.theme-workbench {
  color-scheme: dark;
  --surface-panel: var(--color-dark-card);
  --surface-soft: var(--color-dark-raised);
  --surface-raised: var(--color-dark-raised);
  --text-primary: var(--color-dark-ink);
  --text-secondary: var(--color-dark-secondary);
  --text-muted: var(--color-dark-muted);
  --border-subtle: var(--color-dark-line);
  --border-strong: #344466;
  --semantic-success: var(--color-success-dark);
  --semantic-warning: var(--color-warning-dark);
  --semantic-error: var(--color-error-dark);
  --semantic-info: var(--color-info-dark);
  --focus-color: var(--color-gold);
}

@media (prefers-reduced-motion: reduce) {
  :root {
    --motion-response: 1ms;
    --motion-entry: 1ms;
    --motion-confirm: 1ms;
    --motion-cinematic: 1ms;
  }
}

@media (forced-colors: active) {
  :root,
  .theme-workbench {
    --surface-canvas: Canvas;
    --surface-panel: Canvas;
    --surface-soft: Canvas;
    --surface-raised: Canvas;
    --text-primary: CanvasText;
    --text-secondary: CanvasText;
    --text-muted: GrayText;
    --border-subtle: ButtonBorder;
    --border-strong: ButtonText;
    --semantic-success: LinkText;
    --semantic-warning: CanvasText;
    --semantic-error: Mark;
    --semantic-info: LinkText;
    --focus-color: Highlight;
  }
}
